import { Box, createStyles, makeStyles } from '@material-ui/core';
import cn from 'classnames';
import * as React from 'react';
import BankCardDynamic from '../../../components/BankCard/BankCardDynamic';
import { getBankCardInfoConfirmation } from '../../../components/NegotiationDebts/getBankCardInfoConfirmation';
import useDictionary from '../../../dictionaries/context/useDictionary';
import { BankType, NegotiationOption, NegotiationOptionAlternative, NegotiationType, OutDebt } from '../../../generated/graphql';
import { getExtraN26Suffix } from '../../../utils/getExtraN26Suffix';
import { getNegBooleans } from '../../../utils/getNegBooleans';
import { getNegotiationStateLabel, getNegotiationStateLabelWithNegType } from '../../../utils/getNegotiationStateLabel';
import { TranquiTheme } from '../../App/theme';
import DebtDetailNegConfirmationPayBtn from '../DebtDetailNegConfirmationPayBtn';
import TitleAndSubTitleText from '../TitleAndSubtitleText';
import NewPaymentTermsRowsConnected from './NewPaymentTermsRowsConnected';
import { useSpace } from '../../../content/utils/useSpace';

interface ConfirmationPaymentTermsProps {
  negOption: NegotiationOption;
  outDebt: OutDebt;
  className?: string;
  alternative?: NegotiationOptionAlternative;
  children?: any;
}

const ConfirmationPaymentTerms = (props: ConfirmationPaymentTermsProps) => {
  const { className, negOption,
    outDebt, alternative, children } = props;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const { gT } = useDictionary();
  const {
    isTotalPayment, isACPK, isConfirmedByUser, isApprovedByBank, isPunished,
    isRejected, isNanocredit, isPaymentPromise, isMovistarMoney, isPichincha,
    isBogota, isBogotaTraditional, isCatchUp,
  } = getNegBooleans(outDebt);

  const isMovistarCatchup = isMovistarMoney && isCatchUp && !isConfirmedByUser;
  const showPayBtn = !isMovistarCatchup && (isConfirmedByUser
    || (isApprovedByBank && isACPK)
    || isNanocredit
    || (isMovistarMoney && !isApprovedByBank)
    || (!isPunished && (!isBogota && !isBogotaTraditional))
    || (isPichincha && isTotalPayment && isRejected));

  const bankCardDynamic = (
    <BankCardDynamic
      debt={outDebt}
      info={getBankCardInfoConfirmation(outDebt)}
      className={classes.debtCard}
    >
      {showPayBtn && (
        <Box className={classes.paymentBtn}>
          <DebtDetailNegConfirmationPayBtn
            outDebt={outDebt}
            prePayment={isTotalPayment || isNanocredit
              ? alternative?.value
              : alternative?.prePayment || undefined}
            className="mt-1"
            negOption={negOption}
          />
        </Box>
      )}
    </BankCardDynamic>
  );

  // space implementation
  const spaceContext = {
    outDebt,
    option: negOption,
    alternative,
  };
  const negOptionTitleSpace = useSpace('NEG_OPTION', 'title', spaceContext);

  if (isPichincha
    && isTotalPayment
    && (isConfirmedByUser || isRejected)) return bankCardDynamic;
  if (!isPunished && isRejected && !isPaymentPromise
    && !isTotalPayment && !isACPK) return bankCardDynamic;
  if ((isRejected && !isMovistarMoney) || (isMovistarMoney && isApprovedByBank)) return null;

  const n26Suffix = getExtraN26Suffix(outDebt, negOption);
  const isBogotaBalanceBehindPayment = outDebt?.bank === BankType.BancoDeBogota
  && negOption.type === NegotiationType.BalanceBehindPayment;
  const bogotaBalanceBehindPaymentSubType = isBogotaBalanceBehindPayment && alternative?.type
    ? `_${alternative.type}`
    : '';

  const negotiationBogotaLabel = isBogota && gT('negotiation_label');
  const negotiationStateLabel = negotiationBogotaLabel || getNegotiationStateLabel('' as any);

  return (
    <Box
      className={cn(classes.root, className)}
      data-test="cy-confirmationPaymentTerms-box"
    >
      <TitleAndSubTitleText
        title={`${negotiationStateLabel} ${gT(negOptionTitleSpace.keyIf || `negotiation_${negOption.type}${n26Suffix}_label${bogotaBalanceBehindPaymentSubType}`)}`}
        subtitle={`${getNegotiationStateLabelWithNegType(outDebt)}`}
        className="mb-2"
        secondHeader
      />
      {children}
      <NewPaymentTermsRowsConnected
        className="mb-2"
        outDebt={outDebt}
        negOption={negOption}
      />
      {bankCardDynamic}
    </Box>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  root: {
    width: '100%',
  },
  debtCard: {
    maxWidth: 'initial',
    transition: 'all .4s',
    gridTemplate: 'repeat(2, auto) / 1.5fr 1fr',
    minHeight: 190,
    [theme.breakpoints.up('sm')]: {
      gridTemplate: 'repeat(2, auto) / 2fr 1fr',
      padding: theme.spacing(3),
    },
  },
  paymentBtn: {
    gridColumn: '1 / 3',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default ConfirmationPaymentTerms;
